import { type CacheDatum, CacheDatumState, type LoadedCacheDatum } from "@/cache/CacheDatumState";

const OBJECT_OVERHEAD_BYTES = 32;
const ARRAY_OVERHEAD_BYTES = 24;
const ENTRY_REFERENCE_BYTES = 8;
const MAP_ENTRY_OVERHEAD_BYTES = 16;
const SET_ENTRY_OVERHEAD_BYTES = 8;

/**
 * Estimate the in-memory size of an object graph in bytes.
 * The result is intentionally approximate and is only used to drive cache eviction heuristics.
 */
function estimateObjectByteSize(value: object, seen: WeakSet<object>): number {
  if (seen.has(value)) {
    return 0;
  }
  seen.add(value);

  if (value instanceof ArrayBuffer) {
    return value.byteLength;
  }

  if (ArrayBuffer.isView(value)) {
    return value.byteLength;
  }

  if (typeof Blob !== "undefined" && value instanceof Blob) {
    return value.size;
  }

  if (typeof ImageData !== "undefined" && value instanceof ImageData) {
    return value.data.byteLength + OBJECT_OVERHEAD_BYTES;
  }

  if (typeof ImageBitmap !== "undefined" && value instanceof ImageBitmap) {
    return value.width * value.height * 4;
  }

  if (value instanceof Date) {
    return 8;
  }

  if (Array.isArray(value)) {
    return value.reduce(
      (total, item) => total + ENTRY_REFERENCE_BYTES + estimateValueByteSize(item, seen),
      ARRAY_OVERHEAD_BYTES,
    );
  }

  if (value instanceof Map) {
    let total = OBJECT_OVERHEAD_BYTES;
    for (const [key, entryValue] of value.entries()) {
      total += MAP_ENTRY_OVERHEAD_BYTES + estimateValueByteSize(key, seen) + estimateValueByteSize(entryValue, seen);
    }
    return total;
  }

  if (value instanceof Set) {
    let total = OBJECT_OVERHEAD_BYTES;
    for (const entryValue of value.values()) {
      total += SET_ENTRY_OVERHEAD_BYTES + estimateValueByteSize(entryValue, seen);
    }
    return total;
  }

  let total = OBJECT_OVERHEAD_BYTES;
  for (const [key, entryValue] of Object.entries(value as Record<string, unknown>)) {
    total += key.length * 2 + ENTRY_REFERENCE_BYTES + estimateValueByteSize(entryValue, seen);
  }
  return total;
}

/**
 * Estimate the approximate size of a JS value in bytes.
 * Shared references are counted once per traversal via `seen`.
 */
export function estimateValueByteSize(value: unknown, seen: WeakSet<object> = new WeakSet<object>()): number {
  switch (typeof value) {
    case "string":
      return value.length * 2;
    case "number":
      return 8;
    case "bigint":
      return 8;
    case "boolean":
      return 4;
    case "undefined":
    case "function":
    case "symbol":
      return 0;
    case "object":
      if (value == null) {
        return 0;
      }
      return estimateObjectByteSize(value, seen);
    default:
      return 0;
  }
}

/**
 * Return the cached byte estimate for a loaded entry, computing one from the payload when missing.
 */
export function loadedCacheDatumEstimatedByteSize<T>(cacheLine: LoadedCacheDatum<T>): number {
  return Math.max(1, cacheLine.estimatedByteSize ?? estimateValueByteSize(cacheLine.payload));
}

/**
 * Return the estimated footprint of any cache datum.
 * Pending entries do not contribute to the current retained-size estimate.
 */
export function cacheDatumEstimatedByteSize<T, B, C>(cacheLine: CacheDatum<T, B, C> | undefined): number {
  if (!cacheLine) {
    return 0;
  }

  switch (cacheLine.state) {
    case CacheDatumState.Pending:
      return 0;
    case CacheDatumState.Loaded:
      return loadedCacheDatumEstimatedByteSize(cacheLine);
    case CacheDatumState.PermanentlyFailed:
      return 1;
    default: {
      const _exhaustive: never = cacheLine;
      return _exhaustive;
    }
  }
}

/**
 * Sum the estimated retained size of all loaded entries in a cache record.
 */
export function sumLoadedCacheMemoryConsumption<T, B = null, C = null>(
  cache: Record<string, CacheDatum<T, B, C>>,
): number {
  let total = 0;

  for (const cacheLine of Object.values(cache)) {
    if (cacheLine?.state === CacheDatumState.Loaded) {
      total += loadedCacheDatumEstimatedByteSize(cacheLine);
    }
  }

  return total;
}

/**
 * Build a loaded cache datum and attach an initial estimated payload size.
 */
export function createLoadedCacheDatum<T>(payload: T, requestDatetime: number): LoadedCacheDatum<T> {
  return {
    requestDatetime,
    lastUsed: Number.NEGATIVE_INFINITY,
    state: CacheDatumState.Loaded,
    payload,
    estimatedByteSize: estimateValueByteSize(payload),
  };
}
